import React, { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { HERO_SLIDES } from "../lib/heroSlides";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1.05, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export const HeroSlider = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, duration: 32 });
  const [selected, setSelected] = useState(0);
  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  const onSelect = useCallback(() => { if (embla) setSelected(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => embla.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [embla, onSelect]);

  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 z-0" ref={emblaRef} data-testid="hero-slider">
        <div className="flex h-full">
          {HERO_SLIDES.map((s, i) => (
            <div key={i} className="relative flex-[0_0_100%] min-w-0 h-screen">
              <img src={s.img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/40" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-ink-900/75 to-transparent" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 min-h-screen flex flex-col items-center justify-center text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="overline text-xs text-gold mb-6">
          {t.hero.overline}
        </motion.p>

        <h1 className="font-bookman font-bold uppercase text-white leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-8xl xl:text-[8.5rem] w-full gold-text-shadow">
          {lines.map((line, i) => (
            <span key={i} className="hero-line-mask">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                {i === 2 ? <span className="text-gold">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="mt-7 max-w-xl text-base md:text-lg text-white/70 font-light">
          {t.hero.sub}
        </motion.p>
      </div>

      {/* Buttons pinned at bottom */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 1 }} className="absolute z-20 bottom-24 md:bottom-28 inset-x-0 flex flex-col sm:flex-row gap-4 justify-center items-center px-6">
        <button onClick={() => navigate("/vehicules")} data-testid="hero-book-cta" className="group inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-champagne text-black overline text-xs px-8 py-4 transition-colors">
          {t.hero.cta}
          <ArrowRight weight="light" className="group-hover:translate-x-1 transition-transform" size={18} />
        </button>
        <button onClick={() => navigate("/offres")} data-testid="hero-services-cta" className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-gold text-white overline text-xs px-8 py-4 transition-colors">
          {t.hero.cta2}
        </button>
      </motion.div>

      {/* Slider controls */}
      <div className="absolute z-20 bottom-10 right-6 lg:right-12 flex items-center gap-3">
        <button onClick={() => embla && embla.scrollPrev()} data-testid="hero-prev" className="w-11 h-11 rounded-full border border-white/25 hover:border-gold text-white flex items-center justify-center transition-colors"><CaretLeft size={18} /></button>
        <button onClick={() => embla && embla.scrollNext()} data-testid="hero-next" className="w-11 h-11 rounded-full border border-white/25 hover:border-gold text-white flex items-center justify-center transition-colors"><CaretRight size={18} /></button>
      </div>
      <div className="absolute z-20 bottom-10 left-6 lg:left-12 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => embla && embla.scrollTo(i)} data-testid={`hero-dot-${i}`} className={`h-1 rounded-full transition-all ${selected === i ? "w-8 bg-gold" : "w-4 bg-white/40"}`} />
        ))}
      </div>
    </section>
  );
};
